import { useState } from 'react';
import { ExpandMoreOutlined } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material';
import { ChoiceControl, HelpPopover, SearchField, ValueBadge } from '../../design-system';
import type { AdministratorPermissionOption } from './administratorModel';
import { permissionLabel } from './permissionLabels';

type Group = { label: string; permissions: AdministratorPermissionOption[] };

export function RolePermissionSelector({ groups, value, disabled, onToggle }: {
  groups: Group[];
  value: string[];
  disabled: boolean;
  onToggle: (code: string, checked: boolean) => void;
}) {
  const [query, setQuery] = useState('');
  const [showCodes, setShowCodes] = useState(false);
  const [expanded, setExpanded] = useState<string[]>([]);
  const normalized = query.trim().toLocaleLowerCase();
  const visibleGroups = groups.map((group) => ({ ...group, permissions: group.permissions.filter((permission) =>
    `${group.label} ${permissionLabel(permission.permissionCode)} ${permission.permissionCode}`.toLocaleLowerCase().includes(normalized),
  ) })).filter((group) => group.permissions.length);
  const selectedGroups = groups.filter((group) => group.permissions.some((permission) => value.includes(permission.permissionCode)));

  return <Stack spacing={2}>
    <Stack spacing={0.75}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle2">허용할 업무 <Typography component="span" variant="caption" color="text.secondary">{value.length}개 선택</Typography></Typography>
        <HelpPopover title="허용할 업무">선택한 업무의 관리 범위는 전체 회사입니다.</HelpPopover>
      </Stack>
      <Stack direction="row" useFlexGap spacing={0.75} sx={{ flexWrap: 'wrap' }} aria-label="선택한 업무 영역">
        {selectedGroups.map((group) => <ValueBadge key={group.label} label={`${group.label} ${group.permissions.filter((permission) => value.includes(permission.permissionCode)).length}개`} />)}
        {!value.length && <Typography variant="caption" color="text.secondary">아직 선택한 권한이 없습니다.</Typography>}
      </Stack>
    </Stack>
    <SearchField label="권한 검색" hideLabel={false} placeholder="업무 이름 또는 권한 코드 검색" value={query} onValueChange={setQuery} disabled={disabled} />
    <ChoiceControl kind="checkbox" label="권한 코드 보기" checked={showCodes} onCheckedChange={setShowCodes} />
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, alignItems: 'start' }}>
      {visibleGroups.map((group) => <Accordion
        key={group.label}
        expanded={Boolean(normalized) || expanded.includes(group.label)}
        onChange={(_, open) => setExpanded((current) => open ? [...current, group.label] : current.filter((label) => label !== group.label))}
      >
        <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="subtitle2">{group.label}</Typography>
            <Typography variant="caption" color="text.secondary">{group.permissions.filter((permission) => value.includes(permission.permissionCode)).length} / {group.permissions.length}개 선택</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails><Stack spacing={0.5}>
          {group.permissions.map(({ permissionCode }) => <Stack key={permissionCode}>
            <ChoiceControl kind="checkbox" checked={value.includes(permissionCode)} disabled={disabled} label={permissionLabel(permissionCode)} onCheckedChange={(checked) => onToggle(permissionCode, checked)} />
            {showCodes && <Typography variant="caption" color="text.secondary">{permissionCode}</Typography>}
          </Stack>)}
        </Stack></AccordionDetails>
      </Accordion>)}
      {!visibleGroups.length && <Typography variant="body2" color="text.secondary">검색 결과가 없습니다. 다른 업무 이름으로 검색해 주세요.</Typography>}
    </Box>
  </Stack>;
}
